import React, { FC, useEffect, useRef } from 'react';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import {
  Country,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isSupportedCountry,
  parsePhoneNumber,
} from 'react-phone-number-input/core';
import PhoneInput from 'react-phone-number-input/react-hook-form-input-core';

import { useOutsideClick } from '~src/commons/utils/hooks';
import { useFocus, useOutSideIframeClick } from '~src/commons/utils/hooks/checkout-hooks';
import { useFormFieldScroll } from '~src/commons/utils/hooks/use-form-field-scroll';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useCountryNamesLocalizationQuery } from '~src/country/country-names';
import { useGlobalState } from '~src/global-state';

import { AddressFormFields } from '~address-form/types';
import { stripCountryCallingCode } from '~address-form/utils/address-utils';

import metadata from '~metadata/libphonenumber-metadata.custom.json';

import { InputElementExtension } from '../input-element-extension';
import { FIELD_NAME } from './constants';
import { CountrySelectElement } from './country-select';
import { PhoneNumberInputBase } from './phone-number-input-base';

/**
 * in case we don't support some country in the metadata, we'll use this
 * simple regex to match international phone number spec (ITU-T E.164)
 */
export const GENERIC_PHONE_NUMBER_REGEX = /\+\d{7,15}/;

interface PhoneNumberFieldProps {
  label?: string;
  warning?: boolean;
  validationRules?: RegisterOptions;
  dataTest?: string;
}

export const PhoneNumberInput: FC<PhoneNumberFieldProps> = ({
  validationRules,
  dataTest,
  ...props
}) => {
  const { tICU } = useTranslations();
  const globalState = useGlobalState();
  useCountryNamesLocalizationQuery(); // prefetch data here.

  const { focusProps, isFocused } = useFocus();
  const defaultCountry = globalState.purchase_country as Country;

  const { getValues, setValue, control } = useFormContext<AddressFormFields>();

  const inputRef = useRef<HTMLInputElement>(null);
  useFormFieldScroll(FIELD_NAME, inputRef);
  const mouseDown = useRef<boolean>(false);

  const [country, setCountry] = React.useState<Country>(() => {
    const fieldValue = getValues(FIELD_NAME);
    if (!fieldValue) return defaultCountry;

    const phoneNumber = parsePhoneNumber(fieldValue, metadata);

    return phoneNumber?.country ? phoneNumber.country : defaultCountry;
  });

  useEffect(() => {
    setValue('phone_number_country', country);
  }, []);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const fieldValue = getValues(FIELD_NAME);
    const newCountryCode = e.target.value as Country;

    if (fieldValue) {
      const strippedValue = stripCountryCallingCode(fieldValue, country, metadata);
      setValue(FIELD_NAME, `+${getCountryCallingCode(newCountryCode, metadata)}${strippedValue}`);
    }

    inputRef?.current?.focus();
    setCountry(newCountryCode);
    setValue('phone_number_country', newCountryCode);
  };

  const handleFocus = () => {
    if (!focusProps) {
      return;
    }
    focusProps.onFocus();
  };

  const handleBlur = () => {
    if (!focusProps || mouseDown.current) {
      handleFocus();
      mouseDown.current = false;
    } else {
      focusProps.onBlur();
    }
  };

  const wrapperRef = useOutsideClick(() => {
    handleBlur();
  }, [isFocused]);

  useOutSideIframeClick(() => {
    handleBlur();
  }, [isFocused]);

  return (
    <div ref={wrapperRef} css={{ flexGrow: 1 }}>
      <PhoneInput
        {...props}
        ref={inputRef}
        name={FIELD_NAME}
        control={control}
        country={country}
        onMouseDown={() => (mouseDown.current = true)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        focused={isFocused}
        // we're passing rules instead of validationRules because library component is exported from react-hook-form-input-core
        rules={
          {
            ...validationRules,
            validate: {
              phoneNumber: value => {
                if (!value) {
                  return true;
                } else if (isSupportedCountry(country, metadata)) {
                  /*
                   * since there's a possibility that there's a new range of phone numbers available
                   * and they could be not included in the metadata yet, we're checking only if
                   * the given phone number is possible to exist instead of strict validation
                   */
                  return (
                    isPossiblePhoneNumber(value, metadata) ||
                    tICU('widget.delivery_address.PHONE_NUMBER_FORMAT_VALIDATION')
                  );
                } else {
                  return (
                    GENERIC_PHONE_NUMBER_REGEX.test(value) ||
                    tICU('widget.delivery_address.PHONE_NUMBER_FORMAT_VALIDATION')
                  );
                }
              },
              ...validationRules?.validate,
            },
          } as RegisterOptions<{ phone_number?: string }>
        }
        data-test={dataTest}
        inputExtensionElement={
          <CountrySelectElement country={country} onChange={handleCountryChange} />
        }
        rightElement={
          <InputElementExtension
            name={FIELD_NAME}
            hasValue={!!getValues(FIELD_NAME)}
            setFocused={(focused: boolean) => (focused ? handleFocus() : handleBlur())}
            isFocused={isFocused}
            onBlur={handleBlur}
            tooltipText={tICU('widget.delivery_address.PHONE_NUMBER_TOOLTIP')}
          />
        }
        metadata={metadata}
        international
        countryCallingCode={getCountryCallingCode(country, metadata)}
        inputComponent={PhoneNumberInputBase}
      />
    </div>
  );
};
